import React, { useState } from 'react'
import type { TeamLite, H2HStatsResponse, H2HFilter, H2HMatchStatsOut } from '../../../types'
import { useH2HStats } from '../../../swr-config'
import { getTeamColorWithContrast } from '../../../utils/teamColors'

interface MAH2HTabProps {
  fixtureId: number
  homeTeam: TeamLite
  awayTeam: TeamLite
  onFixtureClick?: (fixtureId: number) => void
}

function getLeagueBadge(leagueName: string | null | undefined): { code: string; color: string } {
  if (!leagueName) return { code: '?', color: 'var(--muted)' }

  const name = leagueName.toLowerCase()
  if (name.includes('premier')) return { code: 'PL', color: '#3d195b' }
  if (name.includes('championship')) return { code: 'CH', color: '#ff6900' }
  if (name.includes('la liga')) return { code: 'LL', color: '#ee8707' }
  if (name.includes('bundesliga')) return { code: 'BL', color: '#d20515' }
  if (name.includes('serie a')) return { code: 'SA', color: '#024494' }
  if (name.includes('ligue 1')) return { code: 'L1', color: '#dae025' }
  if (name.includes('fa cup')) return { code: 'FA', color: '#c8102e' }
  if (name.includes('efl cup') || name.includes('league cup') || name.includes('carabao')) return { code: 'LC', color: '#00a651' }
  if (name.includes('champions')) return { code: 'CL', color: '#0e1e5b' }
  if (name.includes('europa league')) return { code: 'EL', color: '#f68e1f' }
  if (name.includes('conference')) return { code: 'EC', color: '#19b24b' }
  if (name.includes('community shield')) return { code: 'CS', color: '#e31b23' }

  return { code: leagueName.substring(0, 2).toUpperCase(), color: 'var(--muted)' }
}

interface H2HMatchCardProps {
  match: H2HMatchStatsOut
  onFixtureClick?: (fixtureId: number) => void
}

function H2HMatchCard({ match, onFixtureClick }: H2HMatchCardProps) {
  const [expanded, setExpanded] = useState(false)
  const badge = getLeagueBadge(match.league_name)

  // Can only click if we have both a handler AND a valid fixture ID
  const isClickable = Boolean(onFixtureClick && match.api_fixture_id)

  const handleClick = () => {
    if (onFixtureClick && match.api_fixture_id) {
      onFixtureClick(match.api_fixture_id)
    } else if (match.has_stats && !onFixtureClick) {
      // Only allow expand/collapse if not in modal mode
      setExpanded(!expanded)
    }
  }

  return (
    <div
      className="h2h-match-item"
      style={{
        background: expanded ? 'rgba(255,255,255,.05)' : 'rgba(255,255,255,.02)',
        borderRadius: 6,
        marginBottom: 4,
        overflow: 'hidden',
      }}
    >
      <div
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 10px',
          gap: 10,
          cursor: isClickable || (match.has_stats && !onFixtureClick) ? 'pointer' : 'default',
        }}
      >
        <span style={{ color: 'var(--muted)', fontSize: 11, minWidth: 70 }}>{match.date}</span>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, textAlign: 'right', flex: 1 }}>{match.home_team_name}</span>
          <span style={{ fontWeight: 700, color: 'var(--accent)', minWidth: 45, textAlign: 'center', fontSize: 13 }}>
            {match.home_score ?? '-'} - {match.away_score ?? '-'}
          </span>
          <span style={{ fontSize: 12, textAlign: 'left', flex: 1 }}>{match.away_team_name}</span>
        </div>
        <span
          title={match.league_name || 'Unknown'}
          style={{
            background: badge.color,
            color: badge.color === '#dae025' ? '#000' : '#fff',
            fontSize: 9,
            fontWeight: 700,
            padding: '2px 5px',
            borderRadius: 3,
          }}
        >
          {badge.code}
        </span>
        {match.has_stats && !onFixtureClick && (
          <span style={{ color: 'var(--muted)', fontSize: 10, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none' }}>
            ▼
          </span>
        )}
        {isClickable && (
          <span style={{ color: 'var(--muted)', fontSize: 10 }}>→</span>
        )}
      </div>

      {expanded && match.has_stats && !onFixtureClick && (
        <div style={{ padding: '0 10px 10px', borderTop: '1px solid var(--border)' }}>
          <div className="live-stats" style={{ marginTop: 8 }}>
            <StatRow label="Corners" home={match.home_corners} away={match.away_corners} />
            <StatRow label="Shots" home={match.home_shots} away={match.away_shots} />
            <StatRow label="On Target" home={match.home_shots_on_target} away={match.away_shots_on_target} />
            <StatRow label="Possession" home={match.home_possession} away={match.away_possession} suffix="%" />
            <StatRow label="Fouls" home={match.home_fouls} away={match.away_fouls} />
            <StatRow label="Yellow Cards" home={match.home_yellows} away={match.away_yellows} />
            <StatRow label="Red Cards" home={match.home_reds} away={match.away_reds} />
            <StatRow label="Offsides" home={match.home_offsides} away={match.away_offsides} />
            <StatRow label="xG" home={match.home_xg} away={match.away_xg} decimal />
          </div>
          {match.venue && (
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, textAlign: 'center' }}>
              {match.venue}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface StatRowProps {
  label: string
  home?: number | null
  away?: number | null
  suffix?: string
  decimal?: boolean
}

function StatRow({ label, home, away, suffix = '', decimal = false }: StatRowProps) {
  const formatValue = (val?: number | null) => {
    if (val === null || val === undefined) return '-'
    return decimal ? val.toFixed(1) : val.toString()
  }

  return (
    <div className="stat-row">
      <span className="stat-value">{formatValue(home)}{suffix && home != null ? suffix : ''}</span>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{formatValue(away)}{suffix && away != null ? suffix : ''}</span>
    </div>
  )
}

export function MAH2HTab({ fixtureId, homeTeam, awayTeam, onFixtureClick }: MAH2HTabProps) {
  const [filter, setFilter] = useState<H2HFilter>('last5')
  const { data: stats } = useH2HStats(fixtureId, filter)

  if (!stats) {
    return <div className="ma-loading">Loading H2H data...</div>
  }

  const { team1, team2, total_matches, team1_wins, draws, team2_wins, matches, averages, available_filters } = stats as H2HStatsResponse

  if (!team1 || !team2 || !averages) {
    return <div className="ma-loading">H2H data loading...</div>
  }

  const totalWins = team1_wins + team2_wins + draws
  const team1Pct = totalWins > 0 ? (team1_wins / totalWins) * 100 : 0
  const drawsPct = totalWins > 0 ? (draws / totalWins) * 100 : 0
  const team2Pct = totalWins > 0 ? (team2_wins / totalWins) * 100 : 0

  const team1Color = getTeamColorWithContrast(team1.name)
  const team2Color = getTeamColorWithContrast(team2.name)

  const filterLabels: Record<H2HFilter, string> = {
    last3: 'L3',
    last5: 'L5',
    last10: 'L10',
    all: 'All',
  }

  return (
    <>
      <div className="row" style={{ gap: 8, marginTop: 10, marginBottom: 10 }}>
        {available_filters.map((f) => (
          <button
            key={f}
            className={`chip ${filter === f ? 'chip-active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {filterLabels[f]}
          </button>
        ))}
      </div>

      <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
        <div className="card-inner">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
              <span className="badge">
                {team1.crest_url ? <img src={team1.crest_url} alt="" /> : 'H'}
              </span>
              <div>
                <strong style={{ fontSize: 13 }}>{team1.name}</strong>
                <div className="small">{total_matches} matches</div>
              </div>
            </div>
            <div style={{ color: 'var(--muted)', fontWeight: 600, fontSize: 12 }}>vs</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
              <div style={{ textAlign: 'right' }}>
                <strong style={{ fontSize: 13 }}>{team2.name}</strong>
                <div className="small">{total_matches} matches</div>
              </div>
              <span className="badge">
                {team2.crest_url ? <img src={team2.crest_url} alt="" /> : 'A'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
            <span style={{ fontWeight: 600 }}>{team1_wins}W</span>
            <span style={{ fontWeight: 600, color: 'var(--muted)' }}>{draws}D</span>
            <span style={{ fontWeight: 600 }}>{team2_wins}W</span>
          </div>

          <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ width: `${team1Pct}%`, background: team1Color.color, transition: 'width 0.3s' }} />
            <div style={{ width: `${drawsPct}%`, background: 'var(--muted)', transition: 'width 0.3s' }} />
            <div style={{ width: `${team2Pct}%`, background: team2Color.color, transition: 'width 0.3s' }} />
          </div>

          <div className="small" style={{ textAlign: 'center', marginBottom: 8 }}>
            H2H Averages ({averages.matches} matches)
          </div>
          <div className="live-stats">
            <StatRow label="Goals" home={averages.team1_goals} away={averages.team2_goals} decimal />
            <StatRow label="Corners" home={averages.team1_corners} away={averages.team2_corners} decimal />
            <StatRow label="Shots" home={averages.team1_shots} away={averages.team2_shots} decimal />
            <StatRow label="On Target" home={averages.team1_shots_on_target} away={averages.team2_shots_on_target} decimal />
            <StatRow label="Possession" home={averages.team1_possession} away={averages.team2_possession} suffix="%" decimal />
            <StatRow label="Fouls" home={averages.team1_fouls} away={averages.team2_fouls} decimal />
            <StatRow label="Cards" home={averages.team1_cards} away={averages.team2_cards} decimal />
            {averages.team1_xg != null && averages.team2_xg != null && (
              <StatRow label="xG" home={averages.team1_xg} away={averages.team2_xg} decimal />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
              <span style={{ color: 'var(--muted)' }}>Goals</span>
              <span style={{ fontWeight: 600 }}>{averages.total_goals.toFixed(1)}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
              <span style={{ color: 'var(--muted)' }}>Corners</span>
              <span style={{ fontWeight: 600 }}>{averages.total_corners.toFixed(1)}</span>
            </div>
            <div style={{ display: 'flex', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,.05)', fontSize: 11 }}>
              <span style={{ color: 'var(--muted)' }}>Cards</span>
              <span style={{ fontWeight: 600 }}>{averages.total_cards.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div className="small" style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Match History</span>
          <span style={{ color: 'var(--muted)' }}>{total_matches} total</span>
        </div>
        {matches.map((match, idx) => (
          <H2HMatchCard key={match.api_fixture_id ?? `match-${idx}`} match={match} onFixtureClick={onFixtureClick} />
        ))}
      </div>
    </>
  )
}
